import { useQuery } from '@tanstack/react-query'
import { InvitationFirstLoginStage } from '@today/ui-domain'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { consumeInvitationToken, peekInvitationToken } from '@/auth/invitation-token-stash'
import { invitationService } from '@/services/invitation.service'
import { apiErrorCode, destinationPath } from './useInvitationFirstLogin'

/**
 * The federated first login's return leg (TO-309, design D5): the user is now
 * signed in through their SAML IdP; activate them with the stashed link token
 * and land them on their destination.
 */
export function useCompleteFirstLogin(slug: string) {
	const navigate = useNavigate()
	// Read, not consumed: a transient failure (e.g. 503) must stay retryable by reloading.
	const [token] = useState(() => peekInvitationToken(slug))

	const completion = useQuery({
		queryKey: ['invitation-complete', slug, token],
		queryFn: () => invitationService.complete(slug, token as string),
		enabled: token !== null,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
	})

	useEffect(() => {
		if (!completion.data) return
		consumeInvitationToken(slug)
		void navigate(destinationPath(slug, completion.data.destination), { replace: true })
	}, [completion.data, navigate, slug])

	const failed = !token || completion.isError
	return {
		stage: failed ? InvitationFirstLoginStage.ERROR : InvitationFirstLoginStage.REDIRECTING,
		errorCode: completion.isError ? apiErrorCode(completion.error) : null,
	}
}
